import React, { useEffect, useState } from "react";
import { Search, Loader2, X } from "lucide-react";
import useSettings from "../../hooks/useSettings";

const DESIGNATIONS = [
  { label: "Trainee Software Engineer - TSE",    value: "TSE"  },
  { label: "Associate Software Engineer - ASE",  value: "ASE"  },
  { label: "Software Engineer - SDE1",           value: "SDE1" },
  { label: "Senior Software Engineer - SDE2",    value: "SDE2" },
  { label: "Lead Software Engineer - SDE3",      value: "SDE3" },
  { label: "Technical Lead - TL",                value: "TL"   },
  { label: "Team Lead - TML",                    value: "TML"  },
  { label: "Technical Architect - TA",           value: "TA"   },
  { label: "Project Lead - PL",                  value: "PL"   },
  { label: "Associate Project Manager - APM",    value: "APM"  },
  { label: "Project Manager - PM",               value: "PM"   },
  { label: "Business Analyst - BA",              value: "BA"   },
  { label: "Senior Business Analyst - SBA",      value: "SBA"  },
  { label: "Test Engineer - TE",                 value: "TE"   },
  { label: "Senior Test Engineer - STE",         value: "STE"  },
  { label: "Testing Team Lead - TTL",            value: "TTL"  },
  { label: "UX Design Engineer - UXDE",          value: "UXDE" },
  { label: "Senior Design Engineer - SDEG",      value: "SDEG" },
  { label: "Business Development Manager - BDM", value: "BDM"  },
  { label: "Marketing & Sales Executive - MSE",  value: "MSE"  },
  { label: "Human Resources Executive - HR",     value: "HR"   },
  { label: "Senior HR Executive - SHR",          value: "SHR"  },
  { label: "HR Manager - HRM",                   value: "HRM"  },
  { label: "Assistant HR Manager - AHRM",        value: "AHRM" },
];

const SPECIALIZATIONS = [
  "Backend", "Frontend", "FullStack", "Design", "DevOps",
  "QA/Testing", "AI/ML", "Data", "Security", "Cloud",
  "MobileDev", "BusinessAnalyst", "TeamLead", "ProjectManager",
  "Intern", "Network", "Marketing", "Sales", "Finance",
];

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="flex gap-3 items-center">
      <button
        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-l-2 before:border-b-2 before:border-gray-700 before:rotate-45"
        disabled={current === 1}
        onClick={() => onChange(current - 1)}
      >
        ‹
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          className={`w-8 h-8 rounded-lg text-xs ${p === current ? "bg-[#4C5CE7] text-white" : "text-black"}`}
          onClick={() => onChange(p)}
        >{p}</button>
      ))}
      <button
        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-r-2 before:border-t-2 before:border-gray-700 before:rotate-45"
        disabled={current === total}
        onClick={() => onChange(current + 1)}
      >
        ›
      </button>
    </div>
  );
}
// ─── Edit Role Panel ──────────────────────────────────────────────────────────

function EditRolePanel({ resource, onClose, onSave, isSaving }) {
  const specs = Array.isArray(resource.specialization)
    ? resource.specialization
    : resource.specialization ? [resource.specialization] : [];

  const currentDesigLabel =
    DESIGNATIONS.find((d) => d.value === resource.designation)?.label.split(" - ")[0] ||
    resource.designation || "—";

  const [newRole, setNewRole]   = useState(resource.role || "member");
  const [newDesig, setNewDesig] = useState(resource.designation || "");
  const [newSpecs, setNewSpecs] = useState([...specs]);

  const toggleSpec = (s) => {
    setNewSpecs((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const newDesigLabel =
    DESIGNATIONS.find((d) => d.value === newDesig)?.label.split(" - ")[0] ||
    newDesig || "—";

  const handleSave = () => {
    onSave(resource._id, {
      role: newRole,
      designation: newDesig,
      specialization: newSpecs,
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      <div className="fixed top-0 right-0 h-screen w-full max-w-[484px] bg-white z-50 overflow-y-auto shadow-2xl [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="p-8 pb-12">

          {/* Header */}
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="text-xl font-medium">Edit role Assignment</h2>
              <p className="text-xs text-gray-400 mt-1">
                Update role, designation and specializations for this resource
              </p>
            </div>
            <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
          </div>
          <hr className="my-5" />

          {/* User card */}
          <div className="flex items-center gap-4 bg-[#F1F5F9] rounded-lg px-4 py-4 mb-6">
            {resource.profilePicture ? (
              <img src={resource.profilePicture} alt={resource.name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 flex-shrink-0">
                {resource.name?.[0] ?? "?"}
              </div>
            )}
            <div>
              <p className="text-base font-normal capitalize">{resource.name}</p>
              <p className="text-sm text-gray-400 lowercase">{resource.email}</p>
            </div>
          </div>

          {/* Current Assignment */}
          <p className="text-sm font-normal uppercase tracking-wide mb-3">Current Assignment</p>
          <hr className="mb-4" />

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-[#F1F5F9] rounded-lg px-3 py-3">
              <p className="text-sm text-gray-400 capitalize mb-1">Role</p>
              <p className="text-base font-normal capitalize">{resource.role || "—"}</p>
            </div>
            <div className="bg-[#F1F5F9] rounded-lg px-3 py-3">
              <p className="text-sm text-gray-400 capitalize mb-1">Designation</p>
              <p className="text-base font-normal">{currentDesigLabel}</p>
            </div>
          </div>

          <div className="bg-[#F1F5F9] rounded-lg px-3 py-4 mb-6">
            <p className="text-sm text-gray-400 capitalize mb-3">Specialization</p>
            <div className="flex flex-wrap gap-2">
              {specs.length > 0 ? (
                specs.map((s, i) => (
                  <span key={i} className="bg-[#CDDDFA] text-[#4379FE] text-xs px-3 py-1 rounded-full uppercase">
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 text-xs">—</span>
              )}
            </div>
          </div>

          {/* Update Role */}
          <p className="text-sm font-normal uppercase tracking-wide mb-4">Update Role</p>
          <hr className="mb-5" />

          <div className="mb-5">
            <label className="block text-base font-normal mb-2">Change Role</label>
            <div className="relative">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full border border-black/20 rounded-lg px-3 h-[40px] text-sm text-black/50 outline-none appearance-none bg-white pr-10"
              >
                <option value="member">Member</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
              <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-black" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-base font-normal mb-2">Change Designation</label>
            <div className="relative">
              <select
                value={newDesig}
                onChange={(e) => setNewDesig(e.target.value)}
                className="w-full border border-black/20 rounded-lg px-3 h-[40px] text-sm text-black/50 outline-none appearance-none bg-white pr-10"
              >
                <option value="">Select designation</option>
                {DESIGNATIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label.split(" - ")[0]}
                  </option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-black" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-base font-normal mb-3">Select specializations</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((s) => {
                const selected = newSpecs.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleSpec(s)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-all ${
                      selected ? "bg-[#CDDDFA] border-[#CDDDFA] text-[#4379FE]" : "border-[#CFCFCF] text-gray-400"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? "bg-[#4379FE]" : "border border-gray-400"}`}>
                      {selected && (
                        <svg width="7" height="7" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                    {s.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview of Changes */}
          <div className="bg-[#F1F5F9] rounded-lg px-4 py-4 mb-8">
            <p className="text-sm text-[#4253C7] capitalize mb-3">Preview of Changes</p>
            <div className="space-y-2">
              <div className="flex gap-4">
                <span className="text-xs text-gray-400 w-24 flex-shrink-0">Role</span>
                <span className="text-xs text-gray-400 capitalize">
                  {resource.role} →{" "}
                  <span className={newRole !== resource.role ? "text-[#4253C7]" : "text-gray-400"}>
                    {newRole}
                  </span>
                </span>
              </div>
              <div className="flex gap-4">
                <span className="text-xs text-gray-400 w-24 flex-shrink-0">Designation</span>
                <span className="text-xs text-gray-400">
                  {currentDesigLabel} →{" "}
                  <span className={newDesig !== resource.designation ? "text-[#4253C7]" : "text-gray-400"}>
                    {newDesigLabel}
                  </span>
                </span>
              </div>
              <div className="flex gap-4">
                <span className="text-xs text-gray-400 w-24 flex-shrink-0">Specializations</span>
                <span className="text-xs text-gray-400">
                  {specs.join(", ") || "—"} →{" "}
                  <span className="text-[#4253C7]">{newSpecs.join(", ") || "—"}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 h-[40px] border border-[#4253C7] text-[#4253C7] rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 h-[40px] bg-[#4253C7] text-white rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isSaving && <Loader2 size={14} className="animate-spin" />}
              Save
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

// ─── Roles Component ──────────────────────────────────────────────────────────

const Roles = () => {
  const {
    resources,
    resPagination,
    isLoadingResources,
    isUpdatingRole,
    loadUsers,
    updateUserRole,
  } = useSettings();

  const [search, setSearch]             = useState("");
  const [page, setPage]                 = useState(1);
  const [editResource, setEditResource] = useState(null);
   const TABLE_MIN_HEIGHT = 360;


  useEffect(() => { loadUsers(1, ""); }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadUsers(1, search); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handlePage = (p) => { setPage(p); loadUsers(p, search); };

  const handleSave = async (userId, updatedData) => {
    const result = await updateUserRole(userId, updatedData);
    if (result.meta.requestStatus === "fulfilled") {
      setEditResource(null); // close panel — redux already updated the table row
    } else {
      alert(result.payload || "Failed to update role.");
    }
  };

  return (
    <div className="bg-white rounded-xl mt-5 p-5 overflow-hidden">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-16">
        <div className="relative w-full sm:w-[313px] h-[37px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-full border rounded-lg pl-10 pr-3 text-sm outline-none"
          />
        </div>
      </div>

      {/* Table */}
<div className="overflow-x-auto overflow-y-auto max-h-[560px] mt-12 rounded-lg" style={{ minHeight: TABLE_MIN_HEIGHT }}>        {isLoadingResources ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#4253C7]" size={28} />
          </div>
        ) : resources.length === 0 ? (
          <p className="text-center text-gray-400 py-16">No resources found.</p>
        ) : (
          <table className="w-full table-fixed min-w-[640px] lg:min-w-0">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="text-gray-400 text-xs border-b">
                <th className="pb-3 font-normal text-left w-[22%]">Resource</th>
                <th className="pb-3 font-normal text-center w-[12%]">Role</th>
                <th className="pb-3 font-normal text-center w-[20%]">Designation</th>
                <th className="pb-3 font-normal text-center w-[38%]">Specialization</th>
                <th className="pb-3 font-normal text-center w-[8%]">Action</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => {
                const specs = Array.isArray(r.specialization)
                  ? r.specialization
                  : r.specialization ? [r.specialization] : [];

                return (
                  <tr key={r._id} className="border-b text-sm h-[49px]">
                    <td className="py-[14px]">
                      <div className="flex items-center gap-2">
                        {r.profilePicture ? (
                          <img src={r.profilePicture} alt={r.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                            {r.name?.[0] ?? "?"}
                          </div>
                        )}
                        <div>
                          <p className="font-normal text-xs text-black">{r.name || "—"}</p>
                          <p className="text-[10px] text-gray-400">{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-xs capitalize text-center">{r.role}</td>
                    <td className="text-xs text-center">
                      {DESIGNATIONS.find((d) => d.value === r.designation)?.label.split(" - ")[0] || r.designation || "—"}
                    </td>
                    <td className="text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {specs.length > 0 ? (
                          specs.map((s, i) => (
                            <span key={i} className="bg-blue-100 text-blue-600 text-[10px] px-3 py-1 rounded-full uppercase font-normal">
                              {s}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => setEditResource(r)}
                        className="bg-[#E9EDF7] text-xs text-black opacity-60 px-3 py-1 rounded"
                      >
                        EDIT
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {resPagination.totalUsers > 0 && (
        <div className="bg-[#F5F5F5] border-t border-[#F1F1F1] h-[67px] px-6 -mx-5 -mb-5 mt-10 flex justify-between items-center gap-4 text-xs text-gray-500">
          <span>
            Showing {resPagination.showing?.from} to {resPagination.showing?.to} of{" "}
            {resPagination.showing?.of} Results
          </span>
          <Pagination current={page} total={resPagination.totalPages} onChange={handlePage} />
        </div>
      )}

      {/* Edit Panel */}
      {editResource && (
        <EditRolePanel
          resource={editResource}
          onClose={() => setEditResource(null)}
          onSave={handleSave}
          isSaving={isUpdatingRole}
        />
      )}

    </div>
  );
};

export default Roles;